import { useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useUser } from '../api/user/useUser';
import { intercomReady, showArticle } from '../components/IntercomBubble/IntercomBubble';

const PENDING_INTERCOM_ARTICLE_KEY = 'pendingIntercomArticle';

function isValidIntercomArticleId(articleId: string): boolean {
  return /^\d+$/.test(articleId);
}

function getIntercomArticleFromUrl(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('intercomArticle');
}

function storePendingIntercomArticle(articleId: string) {
  window.sessionStorage.setItem(PENDING_INTERCOM_ARTICLE_KEY, articleId);
}

function getPendingIntercomArticle(): string | null {
  return window.sessionStorage.getItem(PENDING_INTERCOM_ARTICLE_KEY);
}

function clearPendingIntercomArticle() {
  window.sessionStorage.removeItem(PENDING_INTERCOM_ARTICLE_KEY);
}

function captureIntercomArticleFromUrl() {
  const articleId = getIntercomArticleFromUrl();
  if (articleId && isValidIntercomArticleId(articleId)) {
    storePendingIntercomArticle(articleId);
  }
}

captureIntercomArticleFromUrl();

export const useIntercomArticleDeepLink = () => {
  const search = useSearch({ strict: false });
  const { isAuthenticated, isAuthLoading } = useUser();
  const hasOpenedArticle = useRef(false);

  useEffect(() => {
    const articleFromUrl = search.intercomArticle || getIntercomArticleFromUrl();
    if (articleFromUrl && isValidIntercomArticleId(articleFromUrl)) {
      storePendingIntercomArticle(articleFromUrl);
    }
  }, [search.intercomArticle]);

  useEffect(() => {
    if (isAuthLoading || hasOpenedArticle.current) {
      return;
    }

    if (!isAuthenticated) {
      return;
    }

    const pendingArticle = getPendingIntercomArticle();

    if (pendingArticle && isValidIntercomArticleId(pendingArticle)) {
      hasOpenedArticle.current = true;
      clearPendingIntercomArticle();

      intercomReady.then(() => {
        showArticle(pendingArticle);
      });
    }
  }, [isAuthenticated, isAuthLoading]);
};
